import React, { useContext, useEffect, useMemo, useState } from 'react';
import { RowNode } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { TextField, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { AIIllustrationItemsModel } from 'models/AIIllustrationItemsModel';
import { AIIllustrationModel } from 'models/AIIllustrationModel';
import { IAIIllustrationItem, AIIllustrationItemStatus } from 'types/aiIllustration';
import { useForceRender } from 'hooks/useForceRender';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import AIIllustrationSummary from './AIIllustrationSummary';
import { IFields } from 'CMFW/grid/fields';
import { asyncTaskStatusRenderer } from 'CMFW/grid/cellRenderers/asyncTaskStatus';
import { pngImageRenderer } from 'CMFW/grid/cellRenderers/pngImageRenderer';
import { aiIllustrationItemStatusRenderer } from 'CMFW/grid/cellRenderers/aiIllustrationItemStatus';

let currentFilter: { type: string; value: string } | null = null;
let aiIllustrationTextFilter = '';

const AIIllustrationVocabularyDetails: React.FC = () => {
  const model = useContext(ModuleContext) as AIIllustrationModel;
  const jobId = model.itemId;

  const forceRender = useForceRender();
  const [activeFilter, setActiveFilter] = useState<{ type: string; value: string } | null>(null);
  const [jobName, setJobName] = useState<string>('');
  const [categoryName, setCategoryName] = useState<string>('');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  currentFilter = activeFilter;

  const itemsModel = useMemo(() => new AIIllustrationItemsModel({ render: forceRender }), [forceRender]);

  useEffect(() => {
    if (jobId > 0) {
      // setJobId calls getGrid internally, which starts polling if needed
      itemsModel.setJobId(jobId);
      setActiveFilter(null);

      // Only read values when loading is complete to avoid race condition
      // where jobId is set but item data hasn't been fetched yet
      if (!model.loadingItem) {
        const name = model.getItemValue('name');
        const categoryName = model.getItemValue('category_name');

        if (name) {
          setJobName(name);
        }
        if (categoryName) {
          setCategoryName(categoryName);
        }
      }
    }

    // Cleanup: stop polling on unmount or when jobId changes
    return () => {
      itemsModel.stopPolling();
    };
  }, [jobId, model, model.loadingItem]); // Added model.loadingItem to re-run when data is loaded

  useEffect(() => {
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [activeFilter, itemsModel]);

  // Debounce text filter input
  useEffect(() => {
    const timer = setTimeout(() => setFilterText(filterInput), 300);
    return () => clearTimeout(timer);
  }, [filterInput]);

  // Update module-level variable and trigger grid re-filtering
  useEffect(() => {
    aiIllustrationTextFilter = filterText;
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [filterText, itemsModel]);

  const isFilterActive = () => currentFilter !== null || aiIllustrationTextFilter.trim() !== '';

  const isRowVisible = (node: RowNode) => {
    const data = node.data as IAIIllustrationItem | undefined;
    if (!data) return true;

    // Handle categorical filter (decision/status)
    if (currentFilter) {
      // Handle decision filter (done only)
      if (currentFilter.type === 'decision') {
        if (currentFilter.value === 'done') {
          // STATUS_DONE = 2 - user has selected alternative
          if (data.status !== AIIllustrationItemStatus.DONE) return false;
        }
      }

      // Handle status filter (processing/review/error/pending_review)
      if (currentFilter.type === 'status') {
        if (currentFilter.value === 'processing') {
          if (data.status !== AIIllustrationItemStatus.PENDING && data.status !== AIIllustrationItemStatus.PROCESSING) {
            return false;
          }
        }
        if (currentFilter.value === 'review') {
          if (data.status !== AIIllustrationItemStatus.NEEDS_REVIEW) return false;
        }
        // Unified filter: show both processing (PENDING/PROCESSING) and review (NEEDS_REVIEW) items
        if (currentFilter.value === 'pending_review') {
          if (
            data.status !== AIIllustrationItemStatus.PENDING &&
            data.status !== AIIllustrationItemStatus.PROCESSING &&
            data.status !== AIIllustrationItemStatus.NEEDS_REVIEW
          ) {
            return false;
          }
        }
        if (currentFilter.value === 'error') {
          if (data.status !== AIIllustrationItemStatus.ERROR) return false;
        }
      }
    }

    // Handle text filter
    if (aiIllustrationTextFilter.trim() !== '') {
      const t = aiIllustrationTextFilter.trim();
      let regex: RegExp | null = null;
      try {
        regex = new RegExp(t, 'i');
      } catch {
        regex = null;
      }

      const values = [String(data.voc_id ?? ''), String(data.voc_title ?? '')];

      if (regex) {
        if (!values.some((value) => regex!.test(value))) return false;
      } else {
        const lowered = t.toLowerCase();
        if (!values.some((value) => value.toLowerCase().includes(lowered))) return false;
      }
    }

    return true;
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.async_task_status]: asyncTaskStatusRenderer,
      [IFields.status]: aiIllustrationItemStatusRenderer,
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
      [IFields.original_png]: pngImageRenderer,
      [IFields.alternative_1]: 'pngImageRendererWithActions',
      [IFields.alternative_2]: 'pngImageRendererWithActions',
    }),
    [],
  );

  // Guard render after establishing hooks
  if (!jobId || jobId === -1) {
    return null;
  }

  return (
    <Container>
      <ValidatorHeader
        id={jobId}
        name={jobName}
        idLabel="Job"
        customTitle={
          <>
            {jobName || `Job #${jobId}`}
            {categoryName && <CategoryBadge>Category: {categoryName}</CategoryBadge>}
          </>
        }
        enableMockCall={false}
        itemsModel={itemsModel}
      />

      <AIIllustrationSummary
        itemsModel={itemsModel}
        onFilterChange={setActiveFilter}
        activeFilter={activeFilter}
        onClearFilter={() => setActiveFilter(null)}
      />

      <FilterRow>
        <TextFieldStyled
          variant="outlined"
          size="small"
          placeholder="Filter by text / id / voc title"
          value={filterInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterInput(e.target.value)}
        />
        <IconButton
          size="small"
          onClick={() => {
            setFilterInput('');
            setFilterText('');
          }}
          disabled={!filterInput}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
      </FilterRow>

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid
            cellRenderers={cellRenderers}
            gridOptions={{ rowHeight: 210 }}
            isFilterActive={isFilterActive}
            isRowVisible={isRowVisible}
          />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={model}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default AIIllustrationVocabularyDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CategoryBadge = styled.span`
  margin-left: 12px;
  font-size: 13px;
  color: #666;
  font-style: italic;
  font-weight: normal;
`;

const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
`;

const TextFieldStyled = styled(TextField)`
  flex-grow: 1;
`;

const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
